import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Film, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import GenCard from "../components/GenCard";

const TYPES = [
  ["image", "Image"], ["video", "Video"], ["scene", "Scene"], ["sequence", "Sequence"],
  ["short_film", "Short Film"], ["commercial", "Commercial"], ["music_video", "Music Video"],
  ["trailer", "Trailer"], ["movie", "Movie"],
];

export default function StudioHome() {
  const { user } = useAuth();
  const { locale, t } = useI18n();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/studio/overview").then(({ data }) => setO(data)).catch(() => {});
  }, []);

  const newProject = async (type, title) => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        title: title || (locale === "fr" ? "Nouveau projet" : "Untitled Project"),
        description: idea,
        type,
        target_duration: type === "movie" ? 600 : type === "short_film" ? 300 : 30,
        language: locale,
      });
      nav(`/studio/projects/${data.id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="studio-home">
      <p className="overline">{t("studio.greeting")}, {user.name?.split(" ")[0]}</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl lg:text-5xl">
        {t("studio.ask")}
      </h1>

      <div className="mt-9 max-w-[900px] rounded-2xl border border-[#212230] bg-[#0b0b11] p-6">
        <textarea
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          rows={3}
          data-testid="home-idea-input"
          placeholder={
            locale === "fr"
              ? "Un court-métrage de 5 minutes sur une femme qui découvre une ville abandonnée après une catastrophe…"
              : "A five-minute short film about a woman discovering an abandoned city after a disaster…"
          }
          className="w-full resize-none bg-transparent text-base leading-relaxed outline-none placeholder:text-[#8288a0]"
        />
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={() => newProject("short_film")}
            disabled={busy}
            data-testid="home-create-project"
            className="btn-amber inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-50"
          >
            <Film className="h-4 w-4" /> {locale === "fr" ? "Nouveau film" : "New movie"}
          </button>
          <Link
            to="/studio/create"
            data-testid="home-goto-create"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold"
          >
            <Wand2 className="h-4 w-4" /> {locale === "fr" ? "Générer un plan" : "Generate a shot"}
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {TYPES.map(([code, label]) => (
          <button
            key={code}
            onClick={() => newProject(code, label)}
            data-testid={`home-type-${code}`}
            className="rounded-full border border-[#2a2c3c] px-4 py-2 text-[12px] text-[#9194a6] transition-colors duration-200 hover:border-[#ff4b2b]/60 hover:text-white"
          >
            <Plus className="mr-1 inline h-3 w-3" />
            {label}
          </button>
        ))}
      </div>

      {o && (
        <>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              [locale === "fr" ? "Crédits" : "Credits", Number(o.credits).toLocaleString()],
              [locale === "fr" ? "Projets" : "Projects", o.projects],
              [locale === "fr" ? "Plans" : "Shots", o.shots],
              [locale === "fr" ? "Médias" : "Assets", o.assets],
              [locale === "fr" ? "En cours" : "Running", o.running],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5">
                <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#8288a0]">{label}</p>
                <p className="mt-2 font-display text-2xl font-semibold">{val}</p>
              </div>
            ))}
          </div>

          {o.recent_projects.length > 0 && (
            <div className="mt-14">
              <div className="flex items-center justify-between">
                <p className="overline">{locale === "fr" ? "Projets récents" : "Recent projects"}</p>
                <Link to="/studio/projects" className="mono text-[11px] text-[#ff4b2b]">
                  {locale === "fr" ? "Tout voir" : "View all"} <ArrowRight className="inline h-3 w-3" />
                </Link>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {o.recent_projects.map((p) => (
                  <Link
                    key={p.id}
                    to={`/studio/projects/${p.id}`}
                    data-testid={`home-project-${p.id}`}
                    className="hot-border rounded-xl border border-[#212230] bg-[#0b0b11] p-5"
                  >
                    <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#ff4b2b]">
                      {p.type} · {p.target_duration}s
                    </p>
                    <p className="mt-2 font-display text-base font-medium">{p.title}</p>
                    <p className="mt-2 line-clamp-2 text-[12px] text-[#9194a6]">
                      {p.logline || p.description || "—"}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {o.recent_generations.length > 0 && (
            <div className="mt-14">
              <p className="overline">{locale === "fr" ? "Générations récentes" : "Recent generations"}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {o.recent_generations.slice(0, 8).map((g) => (
                  <GenCard key={g.id} gen={g} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
